import dayjs from 'dayjs'
import { JOURNEY_COLOR, MONO, SANS } from './CareJourneys/journeyTheme'

/**
 * The Care Queue is drawn from the same approved palette and type scale as the
 * Care Journey board (PD-1484 asks for the two tabs to read as one product), so
 * the tokens are shared rather than re-declared. Re-exported here so queue
 * components have a single import.
 */
export { JOURNEY_COLOR as QUEUE_COLOR, MONO, SANS }

/** Row height presets. Persisted per user — see lastBoardStorage. */
export type QueueDensity = 'roomy' | 'compact'

/**
 * Vertical cell padding in px, fed to Mantine's `verticalSpacing`. It has to go
 * through Mantine rather than a Tailwind `py-*` class: `.mantine-Table-td` sets
 * the `padding` shorthand from unlayered CSS, which outranks anything Tailwind
 * emits into `@layer utilities`, so the utility silently loses.
 */
export const ROW_VERTICAL_SPACING: Record<QueueDensity, number> = {
  roomy: 14,
  compact: 7,
}

export type DueSeverity = {
  /** Left edge bar on the row. Neutral unless the task is actually late. */
  bar: string
  /** Aging chip beside the date, e.g. "12d late". Null when nothing is due. */
  chip: { label: string; bg: string; color: string } | null
  /** Quiet text hint for dates that are near but not yet late. */
  hint: string | null
  dateColor: string
}

const NEUTRAL_BAR = JOURNEY_COLOR.ringIdle
const AMBER = '#e0a23c'

/**
 * Urgency of a due date, in the design's three steps: more than a month late is
 * red, any other lateness is amber, everything else is neutral. A completed task
 * is never urgent — it recedes to grey like a settled step on the journey board.
 */
export function dueSeverity(dateStr: string | null, done: boolean): DueSeverity {
  const quiet: DueSeverity = {
    bar: NEUTRAL_BAR,
    chip: null,
    hint: null,
    dateColor: JOURNEY_COLOR.muted,
  }
  if (!dateStr || done) return quiet

  const daysLate = dayjs().startOf('day').diff(dayjs(dateStr).startOf('day'), 'day')

  if (daysLate > 30) {
    return {
      bar: JOURNEY_COLOR.late,
      chip: { label: `${daysLate}d late`, bg: '#fdf0ef', color: JOURNEY_COLOR.late },
      hint: null,
      dateColor: JOURNEY_COLOR.late,
    }
  }
  if (daysLate > 0) {
    return {
      bar: AMBER,
      chip: { label: `${daysLate}d late`, bg: '#fdf6e9', color: JOURNEY_COLOR.warn },
      hint: null,
      dateColor: JOURNEY_COLOR.ink,
    }
  }
  if (daysLate === 0) {
    return {
      bar: AMBER,
      chip: { label: 'Today', bg: '#fdf6e9', color: JOURNEY_COLOR.warn },
      hint: null,
      dateColor: JOURNEY_COLOR.ink,
    }
  }
  const daysAway = -daysLate
  return {
    bar: NEUTRAL_BAR,
    chip: null,
    hint: daysAway <= 7 ? `In ${daysAway} day${daysAway === 1 ? '' : 's'}` : null,
    dateColor: JOURNEY_COLOR.ink,
  }
}

// A value's mark and lettering. `dot` empty means the value is unremarkable
// enough to carry no mark at all — the design reserves colour for the rows that
// need a person, so a settled or unset cell stays plain text.
export type ValueMeta = { dot: string; color: string; weight: number }

/** Status marks, shared by the board's Status cell and the patient chart (PD-1870). */
export const STATUS_META: Record<string, ValueMeta> = {
  Done: { dot: JOURNEY_COLOR.done, color: JOURNEY_COLOR.done, weight: 500 },
  'Working on it': { dot: AMBER, color: JOURNEY_COLOR.warn, weight: 500 },
  Stuck: { dot: JOURNEY_COLOR.late, color: JOURNEY_COLOR.late, weight: 500 },
  'Not started': { dot: '', color: JOURNEY_COLOR.muted, weight: 400 },
}

/** Colour key under the board, mirroring the Care Journeys legend. */
export const QUEUE_LEGEND = [
  { color: JOURNEY_COLOR.late, label: 'Overdue more than a month' },
  { color: AMBER, label: 'Overdue / due today' },
  { color: JOURNEY_COLOR.done, label: 'Complete' },
  { color: NEUTRAL_BAR, label: 'On track / no due date' },
] as const
